import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp } from "lucide-react";

import { Badge } from "../../../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import type { OfficeComparisonRow } from "../lib/api/types";

function PrePost({
  pre,
  current,
  suffix,
}: {
  pre: number | null;
  current: number | null;
  suffix: string;
}) {
  if (pre == null) return <span className="text-neutral-inactive">—</span>;

  if (current == null) {
    return (
      <span className="tabular-nums text-neutral-inactive">
        {pre.toFixed(1)}
        {suffix} (baseline)
      </span>
    );
  }

  // Hours: lower is better. Capacity ratio: higher is better.
  const improved = suffix === "hrs" ? current < pre : current > pre;
  const Arrow = current < pre ? ArrowDown : ArrowUp;

  return (
    <span className="inline-flex items-center gap-1.5 tabular-nums">
      <span className="text-muted-foreground">
        {pre.toFixed(1)}
        {suffix}
      </span>
      <span className="text-muted-foreground">→</span>
      <span className="font-medium text-primary">
        {current.toFixed(1)}
        {suffix}
      </span>
      {improved ? <Arrow className="size-3.5 text-approve" /> : null}
    </span>
  );
}

const columnHelper = createColumnHelper<OfficeComparisonRow>();

const columns: ColumnDef<OfficeComparisonRow, any>[] = [
  columnHelper.accessor("officeName", {
    header: "Office",
    cell: (info) => (
      <span
        className={
          info.row.original.liveOnAthena ? "font-medium text-foreground" : "text-neutral-inactive"
        }
      >
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor("liveOnAthena", {
    header: "Status",
    cell: (info) =>
      info.getValue() ? (
        <Badge variant="outline" className="border-primary/50 bg-amber-dim text-primary">
          Live
        </Badge>
      ) : (
        <Badge
          variant="outline"
          className="border-neutral-inactive/40 bg-neutral-inactive-dim text-neutral-inactive"
        >
          Not yet live
        </Badge>
      ),
  }),
  columnHelper.display({
    id: "firstDecisionTime",
    header: "First Decision Time",
    cell: ({ row }) => (
      <PrePost
        pre={row.original.firstDecisionTimePreHours}
        current={row.original.firstDecisionTimeCurrentHours}
        suffix="hrs"
      />
    ),
  }),
  columnHelper.display({
    id: "finalDecisionTime",
    header: "Final Decision Time",
    cell: ({ row }) => (
      <PrePost
        pre={row.original.finalDecisionTimePreHours}
        current={row.original.finalDecisionTimeCurrentHours}
        suffix="hrs"
      />
    ),
  }),
  columnHelper.display({
    id: "capacityRatio",
    header: "Capacity Ratio (decisions ÷ active analysts)",
    cell: ({ row }) => (
      <PrePost
        pre={row.original.capacityRatioPre}
        current={row.original.capacityRatioCurrent}
        suffix=""
      />
    ),
  }),
];

export function OfficeComparisonTable({ rows }: { rows: OfficeComparisonRow[] }) {
  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id}>
              {group.headers.map((header) => (
                <TableHead key={header.id} className="whitespace-nowrap">
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className="whitespace-nowrap text-sm">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
